/**
 * «📊 Категории обращений» — `#catBlock` + `#catTotals` (ui-blocks.md §6.1).
 *
 * Два режима приходят с бэкенда сразу (`categories_open`, `categories_period`),
 * переключатель `.mini-seg` работает на клиенте. Смена режима закрывает все
 * раскрытые строки, как в макете.
 *
 * Разбивка по подразделениям в шторке считается из списка дел категории
 * (`GET /api/dashboard/list?kpi=category`): отдельного среза по отделениям
 * в `GET /api/dashboard` нет. Ручка отдаёт не более 200 строк, поэтому, когда
 * дел больше, под разбивкой стоит подпись, по скольким она посчитана — рядом
 * с настоящим `row.count` из шапки строки молчать об этом нельзя.
 */

import { Fragment, useState } from 'react';
import { categoryLabel } from '@/lib/categories';
import type { KeyboardEvent } from 'react';
import { useCaseList } from './api';
import { Delta, DeltaSmall } from './Delta';
import {
    appealWord,
    barWidth,
    catDotStyle,
    categoryHighlight,
    dayWord,
    fmtDate,
    num1,
} from './format';
import type {
    CategoriesBlock,
    CategoryRow,
    DashboardRange,
    PeriodValue,
} from './types';
import { LoadingState } from '../ErrorState';

interface DrawerProps {
    row: CategoryRow;
    mode: 'open' | 'period';
    range: DashboardRange;
    period: PeriodValue;
}

/** Шторка категории: разбивка по подразделениям + чипы-сводка. */
function CategoryDrawer({ row, mode, range, period }: DrawerProps) {
    const list = useCaseList('category', { category: row.category, mode }, period);

    const items = list.data?.items ?? [];
    const listTotal = list.data?.total ?? items.length;
    // Ручка отдаёт не больше 200 строк: если их обрезали, разбивка неполная.
    const truncated = items.length < listTotal;

    const counts = new Map<string, number>();
    for (const item of items) {
        const name = item.department_name ?? 'Отделение не указано';
        counts.set(name, (counts.get(name) ?? 0) + 1);
    }
    const departments = [...counts.entries()]
        .map(([name, count]) => ({ name, count }))
        .sort((a, b) => b.count - a.count);
    const max = departments.reduce((m, d) => Math.max(m, d.count), 1);

    return (
        <div className="detail-box">
            <div className="detail-title">
                <span>Разбивка по подразделениям</span>
                <span className="period-note">
                    {mode === 'open'
                        ? `открытые дела на ${fmtDate(range.today)}`
                        : `за ${range.label}`}
                </span>
            </div>

            {list.isLoading && <LoadingState variant="inline" label="Загружаем разбивку…" />}
            {!list.isLoading && departments.length === 0 && (
                <div className="detail-empty">За выбранный период данных нет — выберите период длиннее.</div>
            )}
            {departments.map((dept) => (
                <div className="cat-row" style={{ cursor: 'default' }} key={dept.name}>
                    <span className="cat-name">
                        <span className="txt">{dept.name}</span>
                    </span>
                    <span className="cat-bar">
                        <i style={{ width: barWidth(dept.count, max), background: row.color }} />
                    </span>
                    <span className="cat-num">{dept.count}</span>
                </div>
            ))}
            {truncated && (
                <div className="case-more">
                    Разбивка посчитана по {items.length} делам из {listTotal}: список приходит с
                    сервера не более чем 200 строками.
                </div>
            )}

            <div className="chips">
                <span className="chip">
                    Норматив ответа: <b>{row.deadline_days} {dayWord(row.deadline_days)}</b>
                </span>
                <span className="chip">
                    Ответственный: <b>{row.responsible_dept}</b>
                </span>
                <span className="chip">
                    Открыто: <b>{row.open_count}</b>
                </span>
                {row.overdue > 0 ? (
                    <span className="chip bad">
                        Просрочено: <b>{row.overdue}</b>
                    </span>
                ) : (
                    <span className="chip good">Просрочек нет</span>
                )}
                {row.closed > 0 && (
                    <span className="chip">
                        Обоснованных: <b>{row.justified}</b> из {row.closed}
                    </span>
                )}
                {row.closed > 0 && row.avg_days !== null && (
                    <span className="chip">
                        Ср. срок ответа: <b>{num1(row.avg_days)} дн</b>
                    </span>
                )}
            </div>
        </div>
    );
}

interface Props {
    categoriesOpen: CategoriesBlock;
    categoriesPeriod: CategoriesBlock;
    range: DashboardRange;
    period: PeriodValue;
}

export function CategoryBlock({ categoriesOpen, categoriesPeriod, range, period }: Props) {
    const [mode, setMode] = useState<'open' | 'period'>('open');
    const [expanded, setExpanded] = useState<Record<string, boolean>>({});

    const block = mode === 'open' ? categoriesOpen : categoriesPeriod;
    const rows = block.rows;
    const max = rows.reduce((m, r) => Math.max(m, r.count), 1);

    const switchMode = (next: 'open' | 'period'): void => {
        if (next === mode) return;
        setMode(next);
        setExpanded({});
    };

    const toggle = (category: string): void => {
        setExpanded((prev) => ({ ...prev, [category]: !prev[category] }));
    };

    const onKeyDown = (event: KeyboardEvent<HTMLDivElement>, category: string): void => {
        if (event.key !== 'Enter' && event.key !== ' ') return;
        event.preventDefault();
        toggle(category);
    };

    return (
        <div className="stat-block">
            <div className="block-header">
                <span>📊 Категории обращений</span>
                <span className="mini-seg" id="catModeSeg" role="group" aria-label="Режим категорий">
                    <button
                        type="button"
                        data-mode="open"
                        className={mode === 'open' ? 'active' : undefined}
                        onClick={() => switchMode('open')}
                    >
                        в работе
                    </button>
                    <button
                        type="button"
                        data-mode="period"
                        className={mode === 'period' ? 'active' : undefined}
                        onClick={() => switchMode('period')}
                    >
                        за период
                    </button>
                </span>
            </div>

            <div className="block-body" id="catBlock">
                {rows.length === 0 ? (
                    <div className="detail-empty">
                        За выбранный период обращений не зарегистрировано.
                    </div>
                ) : (
                    rows.map((row) => {
                        const isOpen = Boolean(expanded[row.category]);
                        const highlight = categoryHighlight(row.category);
                        return (
                            <Fragment key={row.category}>
                                <div
                                    className="stat-row"
                                    data-cat={row.category}
                                    role="button"
                                    tabIndex={0}
                                    aria-expanded={isOpen}
                                    onClick={() => toggle(row.category)}
                                    onKeyDown={(event) => onKeyDown(event, row.category)}
                                >
                                    <span className="label">
                                        <span className="chev">▶</span>
                                        <span className="dot" style={catDotStyle(row.color)} />
                                        <span className="txt">{categoryLabel(row.category)}</span>
                                    </span>
                                    <span className="cat-bar">
                                        <i style={{ width: barWidth(row.count, max), background: row.color }} />
                                    </span>
                                    <span className="value">
                                        {highlight ? (
                                            <span className={highlight}>{row.count}</span>
                                        ) : (
                                            row.count
                                        )}
                                        {mode === 'period' && <DeltaSmall delta={row.delta} />}
                                    </span>
                                </div>
                                <div
                                    className={isOpen ? 'drawer open' : 'drawer'}
                                    data-drawer-cat={row.category}
                                >
                                    <div className="drawer-inner">
                                        {isOpen && (
                                            <CategoryDrawer
                                                row={row}
                                                mode={mode}
                                                range={range}
                                                period={period}
                                            />
                                        )}
                                    </div>
                                </div>
                            </Fragment>
                        );
                    })
                )}
            </div>

            <div className="totals-row" id="catTotals">
                {mode === 'open' ? (
                    <>
                        <span>Итого открытых дел</span>
                        <span>
                            <b>{block.total}</b>
                        </span>
                    </>
                ) : (
                    <>
                        <span>Итого за период</span>
                        <span>
                            <b>{block.total}</b> {appealWord(block.total)} · было {block.prev_total}{' '}
                            <span className="change">
                                <Delta delta={block.total - block.prev_total} />
                            </span>
                        </span>
                    </>
                )}
            </div>
        </div>
    );
}
